/**
 * SimplifiedSwipePanel - A mobile panel component with fixed height that pulls down to close.
 *
 * This is a simplified, reusable version of SwipePanel that provides:
 * - Swipe-down only (no swipe-up) gesture handling
 * - Open/closed states with smooth animations
 * - Scrim (backdrop) overlay
 */
import {
  getPanelTransform,
  lockBodyScroll,
  SwipeDirection,
  swipePanelSharedStyles,
  unlockBodyScroll,
  useSwipeGesture,
} from "./swipePanelShared";
import { css } from "@emotion/react";
import { Icon, IconButton, theme } from "@minted/minted-components";
import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const simplifiedSwipePanelStyles = {
  title: css`
    ${theme.typeStyles.bodyMediumStyles};
    ${theme.typeStyles.bodyBoldStyles};
    color: ${theme.colors.gray800};
    flex: 1;
    text-align: center;
  `,
  titleRow: css`
    display: flex;
    justify-content: space-between;
    padding-bottom: ${theme.spacing.x2};
    padding-left: ${theme.spacing.x3};
    padding-right: ${theme.spacing.x3};
    width: 100%;
  `,
};

export const DEFAULT_PANEL_HEIGHT = 320;

export type SimplifiedSwipePanelRef = {
  close: () => void;
  open: () => void;
};

export type SimplifiedSwipePanelProps = {
  children: ReactNode;
  title: string;
};

export const SimplifiedSwipePanel = forwardRef<
  SimplifiedSwipePanelRef,
  SimplifiedSwipePanelProps
>(({ children, title }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setCurrentPosition(DEFAULT_PANEL_HEIGHT);
    lockBodyScroll();
  }, []);

  // Clamp position to prevent swiping up beyond the default height
  const handlePositionChange = useCallback((position: number) => {
    setCurrentPosition(Math.min(position, DEFAULT_PANEL_HEIGHT));
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setCurrentPosition(0);
    unlockBodyScroll();
  }, []);

  // Cleanup body scroll lock on unmount
  useEffect(() => {
    return () => {
      unlockBodyScroll();
    };
  }, []);

  // Expose open/close methods via ref
  useImperativeHandle(
    ref,
    () => ({
      close: handleClose,
      open: handleOpen,
    }),
    [handleClose, handleOpen],
  );

  const { handleScrollableChild, handleStartSwipe } = useSwipeGesture({
    allowedDirections: [SwipeDirection.Down],
    currentPosition,
    minPosition: 0,
    onPositionChange: handlePositionChange,
    onSwipeEnd: handleClose,
    onSwipingChange: setIsSwiping,
  });

  if (!isOpen) {
    return null;
  }

  const containerStyles = swipePanelSharedStyles.container(
    currentPosition,
    isSwiping,
  );
  const panelTransform = getPanelTransform(currentPosition, isSwiping);

  return (
    <>
      <div
        css={[
          swipePanelSharedStyles.scrim,
          swipePanelSharedStyles.scrimVisible,
        ]}
        onClick={handleClose}
      />
      <div css={containerStyles} ref={containerRef} style={panelTransform}>
        <div
          css={swipePanelSharedStyles.swipeArea}
          onMouseDown={handleStartSwipe}
          onTouchStart={handleStartSwipe}
        >
          <div css={swipePanelSharedStyles.swipeHandle} />
        </div>

        <div
          css={[
            swipePanelSharedStyles.content,
            swipePanelSharedStyles.contentScrollable,
          ]}
          onScroll={handleScrollableChild}
        >
          <div css={swipePanelSharedStyles.contentWrapper}>
            <div css={simplifiedSwipePanelStyles.titleRow}>
              <div css={simplifiedSwipePanelStyles.title}>{title}</div>
              <IconButton
                iconType={Icon.types.close}
                onClick={handleClose}
                type="secondary"
              />
            </div>
            {children}
          </div>
        </div>
      </div>
    </>
  );
});

SimplifiedSwipePanel.displayName = "SimplifiedSwipePanel";
